import { computed, inject, Injectable, signal } from '@angular/core';

import { DealerProvider } from '../../../auth/dealer/dealer.provider';
import { I18nService } from '../../i18n/services/i18n.service';
import { ConsentService } from './consent.service';

@Injectable({
  // not providedIn: 'root', so that the selected language cannot be shared between different customers
  providedIn: null,
})
export class GcdmLanguageProvider {
  private readonly consentService = inject(ConsentService);
  private readonly i18nService = inject(I18nService);
  private readonly dealerProvider = inject(DealerProvider);

  readonly languagePickerSelection = signal<string | undefined>(undefined);
  readonly correspondenceLanguage = signal<string | undefined>(undefined);
  readonly lastSavedConsentLanguage = signal<string | undefined>(undefined);

  private readonly consentLanguageBlacklist: Record<string, string[]> = {
    BE: ['de'],
    LU: ['nl', 'de'],
  };

  private readonly correspondenceLanguageBlacklist: Record<string, string[]> = {
    BE: ['de'],
    LU: ['nl'],
  };

  readonly availableConsentLanguagesInThisCountry = computed(() =>
    this.allowedLanguages(this.consentLanguageBlacklist),
  );

  readonly availableCorrespondenceLanguagesInThisCountry = computed(() =>
    this.allowedLanguages(this.correspondenceLanguageBlacklist),
  );

  private allowedLanguages(blacklist: Record<string, string[]>) {
    const activeCountry = this.dealerProvider.activeCountry();
    const blacklistedLanguages = blacklist[activeCountry ?? ''] ?? [];
    return this.gcdmLanguages().filter((language) => !blacklistedLanguages.includes(language));
  }

  readonly gcdmLanguages = computed(() => {
    const consentsMap = this.consentService.nscConsentData();

    return consentsMap
      ? Array.from(consentsMap.languages.keys())
          .map((languageKey) => languageKey.toLowerCase())
          .sort((a, b) => a.localeCompare(b))
      : [];
  });

  /**
   * The language to use for the consents and BMW/Mini ID registration.
   *
   * The first supported language from the following list is used:
   * <ol>
   *   <li>The language chosen from the language picker in this component</li>
   *   <li>The last consent language stored</li>
   *   <li>The configured correspondence language</li>
   *   <li>The language used for the app UI</li>
   *   <li>English</li>
   *   <li>The first available language alphabetically</li>
   * </ol>
   */
  readonly resolvedConsentLanguage = computed<string>(() => {
    const consentLanguages = this.availableConsentLanguagesInThisCountry();
    const selectionAtConsentLanguagePicker = this.languagePickerSelection();
    if (selectionAtConsentLanguagePicker && consentLanguages.includes(selectionAtConsentLanguagePicker)) {
      return selectionAtConsentLanguagePicker;
    }
    const lastSavedConsentLanguage = this.lastSavedConsentLanguage();
    if (lastSavedConsentLanguage && consentLanguages.includes(lastSavedConsentLanguage)) {
      return lastSavedConsentLanguage;
    }
    const correspondenceLanguage = this.correspondenceLanguage();
    if (correspondenceLanguage && consentLanguages.includes(correspondenceLanguage)) {
      return correspondenceLanguage;
    }
    const uiLanguage = this.i18nService.activeLanguage().substring(0, 2);
    if (uiLanguage && consentLanguages.includes(uiLanguage)) {
      return uiLanguage;
    }
    const fallbackLanguage = 'en';
    if (consentLanguages.includes(fallbackLanguage)) {
      return fallbackLanguage;
    }
    return consentLanguages[0];
  });
}
